import Card from '@/components/common/Card'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import React, { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { callApi, ThrowError } from '@/api/callApi'
import { showToastNotification } from '@/utility/helper'
import { useNavigate } from 'react-router-dom'
import { RoutesLink } from '@/utility/RouteLinks'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import type { EmployeeOnBoard, IEmployee } from '@/interface/employeeOnBoard'
import OnboardBasicDetail from '../Employee/EmployeeOnboard/OnboardBasicDetail'
import OnboardAdvanceDetails from '../Employee/EmployeeOnboard/OnboardAdvanceDetails'
import OnboardAddress from '../Employee/EmployeeOnboard/OnboardAddress'
import OnboardBankDetails from '../Employee/EmployeeOnboard/OnboardBankDetails'
import OnboardNominee from '../Employee/EmployeeOnboard/OnboardNominee'
import OnboardDocuments from '../Employee/EmployeeOnboard/OnboardDocuments'
import { API_ENDPOINT } from '@/api/constant'
import { useAppSelector } from '@/store/store'

const EditProfile: React.FC = () => {
    const navigate = useNavigate()
    const loggedInUser = useAppSelector((state) => state.user.user)
    const empId = loggedInUser?.id || ''
    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('tab1')

    const formatDateForInput = (value?: string | null) => value ? value.split('T')[0] : ''

    const normalizeEmployeeForForm = (employee: IEmployee): EmployeeOnBoard => {
        const userDetail = employee.userDetail
        return {
            name: employee.name || '',
            email: employee.email || '',
            phone: employee.phone || '',
            employeeCode: employee.employeeCode || '',
            type: String(employee.type ?? '') as EmployeeOnBoard['type'],
            gender: String(employee.gender ?? '') as EmployeeOnBoard['gender'],
            status: (employee.status || '') as EmployeeOnBoard['status'],
            dob: formatDateForInput(employee.dob),
            joiningDate: formatDateForInput(employee.joiningDate),
            personalEmail: employee.personalEmail || '',
            bloodGroup: employee.bloodGroup || '',
            profilePhoto: employee.profilePhoto || '',
            isMarried: String(Boolean(employee.isMarried)) as never,
            skillType: employee.skillType || '',
            permissions: Array.isArray(employee.permissions) ? employee.permissions.join(',') : employee.permissions || '',
            isAssignedAllSite: employee.isAssignedAllSite ?? false,
            assignedSiteType: employee.assignedSiteType || '',
            departmentId: employee.departmentId || '',
            designationId: employee.designationId || '',
            shiftId: employee.shiftId || '',
            siteId: employee.siteId || '',
            branchId: employee.branchId || '',
            clientId: '',
            fatherName: userDetail?.fatherName || '',
            motherName: userDetail?.motherName || '',
            uanNumber: userDetail?.uanNumber || '',
            aadharNumber: userDetail?.aadharNumber || '',
            esicNumber: userDetail?.esicNumber || '',
            insuranceNumber: userDetail?.insuranceNumber || '',
            pfNumber: userDetail?.pfNumber || '',
            panNumber: userDetail?.panNumber || '',
            drivingLicenseNumber: userDetail?.drivingLicenseNumber || '',
            address: userDetail?.address || '',
            state: userDetail?.state || '',
            city: userDetail?.city || '',
            pinCode: userDetail?.pinCode || '',
            permanentAddress: userDetail?.permanentAddress || '',
            isSameAsPermanentAddress: userDetail?.isSameAsPermanentAddress ?? false,
            permanentState: userDetail?.permanentState || '',
            permanentCity: userDetail?.permanentCity || '',
            permanentPinCode: userDetail?.permanentPinCode || '',
            bankName: userDetail?.bankName || '',
            ifscCode: userDetail?.ifscCode || '',
            accountHolderName: userDetail?.accountHolderName || '',
            accountNumber: userDetail?.accountNumber || '',
            branchAddress: userDetail?.branchAddress || '',
            nomineeName: userDetail?.nomineeName || '',
            nomineePhone: userDetail?.nomineePhone || '',
            nomineeDob: formatDateForInput(userDetail?.nomineeDob),
            nomineeRelation: userDetail?.nomineeRelation || '',
            ecpName: userDetail?.ecpName || '',
            ecpPhone: userDetail?.ecpPhone || '',
            previousCompanyName: userDetail?.previousCompanyName || '',
            previousDesignationName: userDetail?.previousDesignationName || '',
            previousFromDate: formatDateForInput(userDetail?.previousFromDate),
            previousToDate: formatDateForInput(userDetail?.previousToDate),
            currentCompany: userDetail?.currentCompany ?? false,
            ctc: userDetail?.ctc ?? '',
            basicSalary: userDetail?.basicSalary ?? '',
            hra: userDetail?.hra ?? '',
            otherAllowances: userDetail?.otherAllowances ?? '',
            user: { id: employee.id },
            userDetail: userDetail || undefined,
            oauthClients: employee.oauthClients || [],
            clientIds: (employee.oauthClients || []).map(oc => oc.id).filter(Boolean) as string[],
        }
    }

    const methods = useForm<EmployeeOnBoard>()

    // Fetch logged-in user's full employee profile
    useEffect(() => {
        if (!empId) return
        const fetchProfile = async () => {
            setIsLoading(true)
            try {
                const response = await EmployeeOnBoardService.getEmployeeById(empId)
                if (response?.status) {
                    methods.reset(normalizeEmployeeForForm(response.data))
                }
            } catch (error) {
                ThrowError(error)
            } finally {
                setIsLoading(false)
            }
        }
        fetchProfile()
    }, [empId])

    // File upload helper
    const uploadFile = async (file: File): Promise<string | null> => {
        const formData = new FormData()
        formData.append('file', file)
        try {
            const response = await callApi(`${API_ENDPOINT.fileUpload}/upload`, {
                method: 'POST',
                data: formData,
                multipart: true,
            })
            if (response?.status) {
                return response.data.path
            }
            return null
        } catch (error) {
            ThrowError(error)
            return null
        }
    }

    // Submit handler — always edit mode (uses logged-in user's id)
    const onStepSubmit = async (data: Partial<EmployeeOnBoard>, nextTab?: string) => {
        if (!empId) return
        setIsLoading(true)
        try {
            const response = await EmployeeOnBoardService.editEmployee(data, empId)
            if (response?.status) {
                showToastNotification('success', response.message)
                if (nextTab) {
                    setActiveTab(nextTab)
                } else {
                    navigate(RoutesLink.profile)
                }
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleNextStep = async (nextTab: string, fieldsToValidate: (keyof EmployeeOnBoard)[]) => {
        const isValid = await methods.trigger(fieldsToValidate)
        if (isValid) {
            const stepData: Partial<EmployeeOnBoard> = {}
            fieldsToValidate.forEach((field) => {
                (stepData as Record<string, unknown>)[field] = methods.getValues(field)
            })

            // Intercept next tab for EditProfile custom flow
            let targetTab = nextTab
            if (nextTab === 'tab2') {
                targetTab = 'tab3' // Skip permissions, go to advance details
            } else if (nextTab === 'tab8' || nextTab === 'tab9') {
                // If moving to salary details or client tabs (which are hidden), submit final profile form
                onStepSubmit(stepData)
                return
            }

            onStepSubmit(stepData, targetTab)
        }
    }

    // Unused in EditProfile (no Client tab), kept for tab prop shape compatibility
    const handleClientSubmit = async () => {}

    const tabProps = {
        isLoading,
        isProfile: true,
        empId: empId || null,
        handleNextStep,
        uploadFile,
        handleClientSubmit,
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading title="Edit Profile" isBackButtonVisible />
                <Card>
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit((data) => onStepSubmit(data))}>
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

                                <TabsList className="w-max lg:w-full bg-gray-50/50 p-1.5 flex flex-nowrap overflow-x-auto overflow-y-hidden justify-start h-fit gap-1.5 rounded-2xl border border-gray-100/50 shadow-inner mb-6 scroll-bar-hidden">
                                    <TabsTrigger value="tab1" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Basic Details</TabsTrigger>
                                    <TabsTrigger value="tab3" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Advance Details</TabsTrigger>
                                    <TabsTrigger value="tab4" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Address</TabsTrigger>
                                    <TabsTrigger value="tab5" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Bank Details</TabsTrigger>
                                    <TabsTrigger value="tab6" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Nominee</TabsTrigger>
                                    <TabsTrigger value="tab7" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Documents</TabsTrigger>
                                </TabsList>

                                <TabsContent value="tab1">
                                    <OnboardBasicDetail {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab3">
                                    <OnboardAdvanceDetails {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab4">
                                    <OnboardAddress {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab5">
                                    <OnboardBankDetails {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab6">
                                    <OnboardNominee {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab7">
                                    <OnboardDocuments {...tabProps} />
                                </TabsContent>
                            </Tabs>
                        </form>
                    </FormProvider>
                </Card>
            </div>
        </Layout>
    )
}

export default EditProfile
